import type { StyledComponent } from '@emotion/styled';
import styled from '@emotion/styled';
import type { ReactElement } from 'react';
import type { PropsOf } from '@emotion/react';
import * as tokens from '@tokens';
import { shouldForwardProp } from '@utils/styledProps';
import type { ComponentProps } from 'react';
import type { HeadRow } from './HeadRow';

export interface HeadProps extends PropsOf<'thead'> {
  /**
   * The content of the head
   */
  children?: ReactElement<ComponentProps<typeof HeadRow>, 'HeadRow'>;

  /**
   * Specifies the height of the head cells.
   * The s size will allow only 1 line cells and the size m
   * will allow 2 line cells.
   *
   * Defaults to 's'.
   */
  size?: 's' | 'm';
}

const styledHeadPropNames = ['size'] as const;

/**
 * The Table.Head component is used to display the table header.
 *
 * @example
 * <Table.Head>...</Table.Head>
 */
export const Head: StyledComponent<HeadProps> = styled('thead', {
  shouldForwardProp: shouldForwardProp(styledHeadPropNames),
})<HeadProps>`
  & th span {
    -webkit-line-clamp: ${({ size }) => (size === 's' ? 1 : 2)};
    line-clamp: ${({ size }) => (size === 's' ? 1 : 2)};
  }

  & th {
    height: ${({ size }) =>
      size === 's'
        ? tokens.spacer.x6
        : '68px'}; // height of the result cell if it takes 2 lines. Needs to be hardcoded here.
  }
`;

Head.defaultProps = {
  size: 's',
};
